import { AutoAwesome } from "@mui/icons-material";
import { Alert, Typography } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import { getIncompleteQualityEventFieldPaths } from "src/components/QualityEvents/quality-event-conditions";
import {
  canEditQualityEventRecord,
  getQualityEventDraft,
  isQualityEventStepUnlocked,
} from "src/components/QualityEvents/quality-event-draft";
import { QualityEventActivityTable } from "src/components/QualityEvents/QualityEventActivityTable";
import { QualityEventDraftForm } from "src/components/QualityEvents/QualityEventDraftForm";
import { QualityEventFieldValue } from "src/components/QualityEvents/QualityEventFieldValue";
import { useOpenQualityEventAgent } from "src/components/QualityEvents/useOpenQualityEventAgent";
import { QualityEventRecordSkeleton } from "src/components/Skeletons/QualityEventRecordSkeleton";
import { useSnackbar } from "src/components/Snackbar";
import { ActivityTableSkeleton } from "src/components/ui/ActivityTableSkeleton";
import { AiGradientButton } from "src/components/ui/AiGradientButton";
import { StepAccordion } from "src/components/ui/StepAccordion";
import { StepAccordionGroup } from "src/components/ui/StepAccordionGroup";
import { useGetUser, useGetUsersByOrg } from "src/hooks/queries";
import { useSaveQualityEventRecord } from "src/hooks/queries/quality-event-mutations";
import {
  useGetQualityEventAudit,
  useGetQualityEventFiles,
  useGetQualityEventRecord,
} from "src/hooks/queries/quality-events";
import { qualityEventErrorMessage } from "src/services/quality-events";
import {
  isQualityEventApproval,
  QualityEventValue,
} from "src/types/quality-events";
import { getUserDisplayName } from "src/utils/user";

const formatSummaryDate = (date: string) =>
  new Date(date).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export const QualityEventRecordPage = () => {
  const { orgId = "", qualityEventKey = "", recordId = "" } = useParams();
  const openAgent = useOpenQualityEventAgent();
  const recordQuery = useGetQualityEventRecord({ orgId, recordId });
  const users = useGetUsersByOrg({ orgId });
  const files = useGetQualityEventFiles({ orgId });
  const audit = useGetQualityEventAudit({ orgId, recordId });
  const { data: currentUser } = useGetUser();
  const save = useSaveQualityEventRecord({ orgId });
  const { showSnackbar } = useSnackbar();
  const [expandedSteps, setExpandedSteps] = useState<Record<string, boolean>>(
    {}
  );

  if (recordQuery.isLoading || users.isLoading)
    return <QualityEventRecordSkeleton />;
  if (
    recordQuery.data?.qualityEventKey !== qualityEventKey ||
    !users.data ||
    users.isError
  )
    return (
      <Alert severity="error">
        Unable to load this record. Return to Quality Events and try again.
      </Alert>
    );

  const record = recordQuery.data;
  const orgUsers = users.data;
  const steps = record.definition.definition.steps;
  const canEdit =
    canEditQualityEventRecord(record, currentUser) &&
    steps.some((step) => isQualityEventStepUnlocked(record, step.key));
  const summaryFields = steps.flatMap((step) =>
    step.fields
      .filter(
        (field) =>
          field.showInOverview &&
          field.type !== "approval" &&
          field.type !== "repeatable"
      )
      .map((field) => ({
        key: `${step.key}:${field.key}`,
        label: field.label,
        value: (
          <QualityEventFieldValue
            field={field}
            value={
              record.steps[step.key]?.answers[field.key] as
                | QualityEventValue
                | undefined
            }
            files={files.data ?? []}
            orgId={orgId}
            users={orgUsers}
          />
        ),
      }))
  );
  const qualityEventId = qualityEventKey.toUpperCase();
  const currentStepTitle = steps.find(
    (step) => step.key === record.currentStepKey
  )?.title;
  const workflowAnswers = Object.assign(
    {},
    ...Object.values(record.steps).map((step) => step.answers)
  );
  const summaryItems = [
    {
      key: "id",
      label: `${qualityEventId} ID`,
      value: `${qualityEventId}-${recordId.slice(0, 8).toUpperCase()}`,
    },
    {
      key: "status",
      label: "Status",
      value: { OPEN: "Open", IN_REVIEW: "In review", CLOSED: "Closed" }[
        record.status
      ],
    },
    {
      key: "owner",
      label: "Owner",
      value: getUserDisplayName(record.createdBy),
    },
    {
      key: "opened",
      label: "Opened",
      value: formatSummaryDate(record.createdAt),
    },
    ...(record.closedAt
      ? [
          {
            key: "closed",
            label: "Closed",
            value: formatSummaryDate(record.closedAt),
          },
        ]
      : []),
    {
      key: "current-step",
      label: "Current step",
      value:
        record.status === "CLOSED"
          ? "Closed"
          : (currentStepTitle ?? "Not provided"),
    },
    ...summaryFields,
  ];
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb
            items={[
              {
                label: "Quality Event Management",
                to: `/${orgId}/quality-events`,
              },
              {
                label: record.definition.name,
                to: `/${orgId}/quality-events/${qualityEventKey}`,
              },
              {
                label: record.title || `Untitled ${record.definition.name}`,
              },
            ]}
          />
        }
        rightButtons={
          canEdit && (
            <AiGradientButton
              startIcon={<AutoAwesome />}
              onClick={() => openAgent(record.definition, record)}
            >
              Edit
            </AiGradientButton>
          )
        }
      />
      <div className="flex flex-col gap-4 px-3 py-2">
        <section aria-label={`${record.definition.name} summary`}>
          <dl className="m-0 grid grid-cols-1 gap-4 min-[900px]:grid-cols-4">
            {summaryItems.map(({ key, label, value }) => (
              <div key={key} className="min-w-0">
                <Typography
                  component="dt"
                  variant="overline"
                  color="text.secondary"
                  className="leading-[1.2]"
                >
                  {label}
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  className="m-0 mt-1 break-words [&_.MuiTypography-root]:[font:inherit]"
                >
                  {value}
                </Typography>
              </div>
            ))}
          </dl>
        </section>
        <section aria-label="Workflow steps" className="flex flex-col gap-2">
          <Typography
            variant="subtitle1"
            component="h2"
            className="font-semibold"
          >
            Workflow steps
          </Typography>
          <StepAccordionGroup>
            {steps.map((step, index) => {
              const savedStep = record.steps[step.key];
              const completed = !!savedStep?.completedAt;
              const current =
                record.currentStepKey === step.key &&
                record.status !== "CLOSED";
              const owner = savedStep?.owner;
              const ownerName = owner
                ? getUserDisplayName(
                    orgUsers.find((user) => user.id === owner) ?? {}
                  )
                : undefined;
              const accordionKey = `${recordId}:${step.key}`;
              const invalidRequirementPaths =
                current && savedStep?.updatedAt
                  ? Array.from(
                      getIncompleteQualityEventFieldPaths({
                        fields: step.fields,
                        values: savedStep.answers,
                        allAnswers: workflowAnswers,
                        completionRequirements: step.completionRequirements,
                        files: {},
                        requiredFieldPaths: new Set(),
                        validateAllRequirements: true,
                      })
                    )
                  : [];
              const waitingForApproval =
                current &&
                Object.values(savedStep?.answers ?? {}).some(
                  (answer) =>
                    isQualityEventApproval(answer) &&
                    answer.status === "PENDING"
                );
              return (
                <StepAccordion
                  key={accordionKey}
                  stepNumber={index + 1}
                  title={step.title}
                  status={
                    completed ? "complete" : current ? "current" : "locked"
                  }
                  missingRequirementCount={invalidRequirementPaths.length}
                  waitingForApproval={waitingForApproval}
                  subtitle={ownerName && <>Responsible person: {ownerName}</>}
                  actions={
                    expandedSteps[accordionKey] &&
                    canEdit &&
                    isQualityEventStepUnlocked(record, step.key) && (
                      <AiGradientButton
                        startIcon={<AutoAwesome />}
                        onClick={() => openAgent(record.definition, record)}
                      >
                        Edit
                      </AiGradientButton>
                    )
                  }
                  expanded={expandedSteps[accordionKey] ?? false}
                  onExpandedChange={(nextExpanded) =>
                    setExpandedSteps((previous) => ({
                      ...previous,
                      [accordionKey]: nextExpanded,
                    }))
                  }
                  detailsLabel={`${step.title} fields`}
                >
                  <QualityEventDraftForm
                    orgId={orgId}
                    record={record}
                    users={orgUsers}
                    step={step}
                    draft={getQualityEventDraft({ record, step })}
                    formId={`record-${recordId}-${step.key}`}
                    creator={record.createdBy}
                    existingFiles={files.data ?? []}
                    userId={currentUser?.id ?? ""}
                    currentUser={currentUser}
                    pending={save.isPending}
                    error={null}
                    invalidRequirementPaths={invalidRequirementPaths}
                    onApprovalDecision={async (fieldKey, decision) => {
                      try {
                        await save.mutateAsync({
                          recordId,
                          body: {
                            expectedVersion: record.version,
                            stepKey: step.key,
                            answers: { [fieldKey]: decision },
                          },
                        });
                      } catch (error) {
                        showSnackbar(qualityEventErrorMessage(error));
                      }
                    }}
                  />
                </StepAccordion>
              );
            })}
          </StepAccordionGroup>
        </section>
        <section aria-label="Activity history" className="flex flex-col gap-2">
          <Typography
            variant="subtitle1"
            component="h2"
            className="font-semibold"
          >
            Activity history
          </Typography>
          {audit.isLoading ? (
            <ActivityTableSkeleton />
          ) : audit.isError ? (
            <Alert severity="error">Unable to load activity history.</Alert>
          ) : (
            <QualityEventActivityTable entries={audit.data ?? []} />
          )}
        </section>
      </div>
    </div>
  );
};
